import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';

import { ChannelMediaTypeEnum, ChannelScopeEnum, ChannelType } from '../types';

import { channelsQueryKeys } from './query-keys';
import { useUpdateChannelImage } from './update-channel-image';

export type TPatchBroadcastersParams = {
  id: string;
  uri?: string;
  name: string;
  media_type?: ChannelMediaTypeEnum;
  scope?: ChannelScopeEnum;
  av_code?: string | null;
  country_code?: string;
  short_name?: string | null;
  region_code?: string | null;
  subregion_code?: string | null;
  parent_channel_id?: string | null;
  logo?: File | string | null;
};

export async function patchChannel({ ...data }: TPatchBroadcastersParams): Promise<ChannelType> {
  return mediaManagerClient.patch(`/api/v1/channels/${data.id}`, data);
}

type MutationFn = typeof patchChannel;

type TUsePatchBroadcastersParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchChannel({ config }: TUsePatchBroadcastersParams = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { addErrorNotification, addSuccessNotification } = useNotificationStore();
  const { mutate: updateChannelImage } = useUpdateChannelImage();

  return useMutation({
    onError(error) {
      addErrorNotification(t('No se ha podido actualizar la emisora'));
      console.error(error);
    },
    onSuccess(channel, variables) {
      addSuccessNotification(t('La emisora se ha actualizado correctamente'));

      if (variables.logo instanceof File) {
        updateChannelImage({
          id: channel.id,
          logo: variables.logo,
        });
      }

      queryClient.invalidateQueries({ queryKey: channelsQueryKeys.all });
    },
    ...config,
    mutationFn: patchChannel,
  });
}
